import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import { Avatar } from '@components/basics/Images/Avatar';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { type FragmentType, getFragment, ReleaseCreatedPostFragmentDoc } from '@graphql/reactQuery';
import { trackClickEvent } from '@lib/analytics';
import { SPACE } from '@utils/constants';
import { webappUriPath } from '@utils/url';
import { FeedActivityContainer, FeedListUsername } from '../components/new-shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleaseCreatedPost on CreatedPost {
    id
    post {
      id
      content
      createdAt
      creator {
        publicAddress
        username
        webappUri
        avatar {
          id
          url
        }
        artist {
          id
        }
      }
      webappUri
    }
  }
`);

const POST_SUBTEXT = 'posted this song to the feed';
const SUBTEXT_ARR = POST_SUBTEXT.split(' ');

export function ReleaseCreatedPostActivity({
  createdPostFrag,
}: {
  createdPostFrag: FragmentType<ReleaseCreatedPostFragmentDoc>;
}) {
  const { post } = getFragment(ReleaseCreatedPostFragmentDoc, createdPostFrag);

  const trackClick = useStableCallback(() => {
    trackClickEvent({
      event: 'Post Link Clicked',
      properties: {
        associationId: post.id,
        associationType: 'Post',
        sourceComponent: 'Release Activity Feed',
      },
    });
  });

  return (
    <FeedActivityContainer timestamp={post.createdAt}>
      <AccessibleLink pathnameUri={webappUriPath(post.webappUri)} onClick={trackClick}>
        <div className="flex w-full items-center">
          <div className="flex gap-2">
            <div className="hidden h-12 w-12 flex-shrink-0 md:flex">
              <Avatar
                disableLink
                size={48}
                borderRadius={999}
                walletAddress={post.creator.publicAddress}
                webappUri={post.creator.webappUri}
                src={post.creator.avatar?.url}
                username={post.creator.username}
              />
            </div>
            <div className="h-10 w-10 flex-shrink-0 md:hidden">
              <Avatar
                disableLink
                size={40}
                borderRadius={999}
                walletAddress={post.creator.publicAddress}
                webappUri={post.creator.webappUri}
                src={post.creator.avatar?.url}
                username={post.creator.username}
              />
            </div>
            <div className="hidden items-center md:inline-flex">
              <FeedListUsername username={post.creator.username} isArtist={!!post.creator.artist} />
            </div>
          </div>

          <div className="ml-2 inline-flex flex-wrap items-start md:ml-0 md:items-center">
            <div className="inline-flex md:hidden">
              <FeedListUsername username={post.creator.username} isArtist={!!post.creator.artist} />
            </div>
            {SUBTEXT_ARR.map((word, i) => (
              <span key={`${word}-${i}`} className="font-base text-neutral500">
                {word}
                {i !== SUBTEXT_ARR.length - 1 && SPACE}
              </span>
            ))}
          </div>
        </div>
      </AccessibleLink>
    </FeedActivityContainer>
  );
}
